import { useRef, useState, useEffect, Suspense } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Float, MeshDistortMaterial, Sparkles, Environment } from '@react-three/drei';
import { motion } from 'framer-motion';
import * as THREE from 'three';
import { HERO_LEATHER } from '../assets';

// An abstract 3D floating leather piece — Exploding Layers + Mouse Reactive
function FloatingLeather() {
  const groupRef = useRef(null);
  const { viewport } = useThree();
  const isMobile = viewport.width < 5;
  const scale = isMobile ? 0.6 : 1;
  const targetRot = useRef(new THREE.Vector2());

  useFrame((state) => {
    if (groupRef.current) {
      // Mouse rotation target
      const mouseX = (state.pointer.x * Math.PI) / 3;
      const mouseY = (state.pointer.y * Math.PI) / 3;
      
      // Smooth lerp to mouse position
      targetRot.current.x = THREE.MathUtils.lerp(targetRot.current.x, mouseY, 0.08);
      targetRot.current.y = THREE.MathUtils.lerp(targetRot.current.y, mouseX, 0.08);

      // Base idle animation + mouse rotation
      groupRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.5) * 0.1 - targetRot.current.x;
      groupRef.current.rotation.y = Math.cos(state.clock.elapsedTime * 0.3) * 0.1 + targetRot.current.y;
      
      // Exploding layers effect - separate children based on mouse movement
      const spread = Math.abs(state.pointer.x) + Math.abs(state.pointer.y); 
      
      const children = groupRef.current.children;
      if (children.length === 3) {
        // Back layer goes deep
        children[0].position.z = THREE.MathUtils.lerp(children[0].position.z, -0.5 - (spread * 2.5), 0.1);
        // Middle layer stays grounded
        children[1].position.z = THREE.MathUtils.lerp(children[1].position.z, 0, 0.1);
        // Front layer (gold wireframe) comes forward
        children[2].position.z = THREE.MathUtils.lerp(children[2].position.z, 0.5 + (spread * 2.5), 0.1);
      }
    }
  });

  return (
    <Float speed={1.5} rotationIntensity={0.2} floatIntensity={1}>
      <group ref={groupRef} position={[0, 0, -3]} scale={scale}>
        
        {/* Back Layer - Deep leather hide (translucent so leather shows through) */}
        <mesh position={[0, 0, -0.5]}>
          <planeGeometry args={[14, 10, 32, 32]} />
          <MeshDistortMaterial color="#1c1109" roughness={0.9} metalness={0.1} distort={0.5} speed={1.5} transparent opacity={0.32} />
        </mesh>

        {/* Middle Layer - Silking leather sheet (translucent) */}
        <mesh position={[0, 0, 0]}>
          <planeGeometry args={[14.5, 10.5, 32, 32]} />
          <MeshDistortMaterial color="#3a2512" roughness={0.8} metalness={0.3} distort={0.3} speed={1} transparent opacity={0.26} />
        </mesh>

      </group>
    </Float>
  );
}

function CameraRig() {
  const { viewport } = useThree();
  const isMobile = viewport.width < 5;
  const target = useRef(new THREE.Vector3(0, 0, 6));

  useFrame((state) => {
    if (!isMobile) {
      target.current.set(state.pointer.x * 2, state.pointer.y * 1.2, 6);
    } else {
      // Use different frequencies than the leather mesh so they don't cancel out visually
      target.current.set(
        Math.sin(state.clock.elapsedTime * 0.7) * 0.6,
        Math.cos(state.clock.elapsedTime * 0.4) * 0.5,
        6
      );
    }
    state.camera.position.lerp(target.current, 0.05);
    state.camera.lookAt(0, 0, 0);
  });
  return null;
}

// Composes the 3D scene — soft floating leather depth (no wireframe, no 3D text)
function Scene() {
  return (
    <>
      {/* Premium Dramatic Lighting */}
      <ambientLight intensity={0.32} />
      <spotLight position={[5, 10, 5]} angle={0.4} penumbra={1} intensity={120} color="#ffffff" castShadow />
      <spotLight position={[-5, -5, -5]} angle={0.5} penumbra={1} intensity={80} color="#D4AF37" />

      {/* Subtle Ambient Particles (Performant) */}
      <Sparkles count={60} scale={15} size={1.6} speed={0.35} opacity={0.22} color="#C9A96A" />

      {/* Environment Map for soft reflection */}
      <Environment preset="city" />

      {/* 3D Elements — floating leather depth */}
      <FloatingLeather />

      {/* Mouse Tracking Rig */}
      <CameraRig />
    </>
  );
}

export default function Ch1Raw() {
  const [visible, setVisible] = useState(true);
  const sectionRef = useRef(null);

  // Pause the 3D canvas when scrolled past Ch1 to save GPU
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="ch-1" ref={sectionRef} data-testid="chapter-1" className="relative h-screen w-full overflow-hidden bg-[#1a120b]">
      {/* Leather material background */}
      <div
        className="absolute inset-0 z-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_LEATHER})` }}
      />
      {/* Warm tint + vignette — quiet-luxury, leather stays visible, edges framed */}
      <div
        className="absolute inset-0 z-0"
        style={{ background: 'radial-gradient(ellipse at 50% 44%, rgba(28,18,10,0.10) 0%, rgba(16,10,6,0.42) 56%, rgba(4,3,2,0.9) 100%)' }}
      />
      {/* Slow silky sheen sweeping across the leather */}
      <div className="hero-sheen absolute inset-0 z-0 pointer-events-none" />

      {visible && (
        <div className="absolute inset-0 z-10">
          <Canvas
            camera={{ position: [0, 0, 6], fov: 45 }}
            dpr={[1, 1.5]}
            performance={{ min: 0.5 }}
            frameloop="always"
            gl={{ alpha: true }}
            style={{ background: 'transparent' }}
          >
            <Suspense fallback={null}>
              <Scene />
            </Suspense>
          </Canvas>
        </div>
      )}

      {/* Refined leather-vibe typography */}
      <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center px-6 pointer-events-none">
        <motion.span
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="font-accent text-[10px] md:text-[11px] text-[#C9A96A] tracking-[0.42em] mb-7"
        >
          Pakkar Tanveer Exports
        </motion.span>

        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 1, delay: 0.35 }}
          className="w-14 h-px bg-[#B89B72]/50 mb-8"
        />

        <motion.h1
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.45, ease: [0.25, 1, 0.5, 1] }}
          className="font-display font-light text-[2.6rem] leading-[1.08] sm:text-6xl lg:text-7xl tracking-tight text-[#F3ECE1]"
          style={{ textShadow: '0 2px 40px rgba(0,0,0,0.55)' }}
        >
          Every jacket begins
          <br />
          <span className="italic text-[#C9A96A]">as a hide.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.7 }}
          className="font-body font-light text-sm md:text-base text-[#C7B79E] mt-8 max-w-md leading-relaxed"
        >
          Premium leather, crafted over 35 years for the world&rsquo;s most demanding brands.
        </motion.p>
      </div>

      <div className="absolute bottom-12 left-0 w-full flex justify-center pointer-events-none z-20">
        <div className="flex flex-col items-center gap-3">
          <span className="font-accent text-[9px] text-[#F5F5F0]/70 tracking-[0.3em]">SCROLL TO CRAFT</span>
          <div className="w-px h-16 bg-gradient-to-b from-[#C9A96A] to-transparent" />
        </div>
      </div>
    </section>
  );
}
